'use client';

import clsx from 'clsx';
import { motion } from 'framer-motion';
import { FC, useEffect, useRef, useState, useCallback } from 'react';

export const HaloContainer: FC<{
  children: React.ReactNode;
  elementRef?: React.RefObject<HTMLElement>;
  disableAnimation?: boolean;
  backgroundColor?: string | null;
  className?: string;
}> = ({
  children,
  elementRef,
  disableAnimation = false,
  backgroundColor,
  className,
}) => {
  const pseudoBorderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [isInIframe, setIsInIframe] = useState(false);

  useEffect(() => {
    setIsInIframe(window.self !== window.top);
  }, []);

  /*
   * Disable the halo
   */
  const disableHalo = useCallback(() => {
    if (pseudoBorderRef.current) {
      pseudoBorderRef.current.classList.add('hidden');
    }
    if (elementRef?.current) {
      elementRef.current.classList.remove('animate-pulse');
    }
  }, [elementRef]);

  /*
   * Enable the halo
   */
  const enableHalo = useCallback(() => {
    if (pseudoBorderRef.current) {
      pseudoBorderRef.current.classList.remove('hidden');
    }
    if (elementRef?.current) {
      elementRef.current.classList.add('animate-pulse');
    }
  }, [elementRef]);

  useEffect(() => {
    if (containerRef.current) {
      if (pseudoBorderRef.current) {
        pseudoBorderRef.current.classList.remove('hidden');
      }
    }

    /*
     * If the elementRef is not given, we don need to
     * adjust the halo container
     */
    if (!elementRef?.current) {
      return;
    }

    const rect = elementRef.current?.getBoundingClientRect();

    /*
     * If the container is surrounded by a button
     * make sure the halo is applied behind the button
     */

    if (rect && pseudoBorderRef.current) {
      pseudoBorderRef.current.style.height = `${rect.height}px`;
      pseudoBorderRef.current.style.width = `${rect.width}px`;
      pseudoBorderRef.current.style.left = `calc(50% - ${rect.width}px / 2)`;
      pseudoBorderRef.current.style.top = `calc(50% - ${rect.height}px / 2)`;
      pseudoBorderRef.current.classList.add('rounded-md!');
    }
  }, [elementRef]);

  useEffect(() => {
    if (isInIframe) {
      disableHalo();
    }
  }, [isInIframe, disableHalo]);

  /*
   * Animation toggle
   */
  useEffect(() => {
    if (disableAnimation === true) {
      disableHalo();
    } else {
      enableHalo();
    }
  }, [disableAnimation, disableHalo, enableHalo]);

  const variants = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        delay: 0.5,
        stiffness: 100,
      },
    },
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={variants}
      className="relative inset-0 z-10"
    >
      <div className={clsx('HaloContainer', className)} ref={containerRef}>
        {children}
        <div
          ref={pseudoBorderRef}
          className={clsx(
            elementRef
              ? 'rounded-md before:rounded-md after:rounded-md'
              : 'rounded-2xl before:rounded-2xl after:rounded-2xl',
            'pseudo-border relative z-0 rounded-lg text-center',
          )}
          style={{ zIndex: -1, backgroundColor: backgroundColor || undefined }}
        >
          <div
            className={clsx(
              elementRef ? 'rounded-md' : 'rounded-2xl',
              'relative z-1 h-full',
            )}
            style={{
              backgroundColor: backgroundColor || undefined,
              zIndex: 1,
            }}
          ></div>
        </div>
      </div>
    </motion.div>
  );
};
